/**
 * VolAnalysisTab.jsx — earnings/VolAnalysisTab.jsx
 *
 * The "Vol Analysis" inner tab. Shows:
 *   - VIX term structure snapshot (spot, 1M fut, 2M fut, contango/backwardation)
 *   - Top vol trade idea of the week (highlighted banner)
 *   - Market regime summary
 *   - 5-7 individual equity/ETF vol setup cards
 *
 * Data is AI-generated by Claude Haiku using SYSTEM_VOLANALYSIS prompt in the Worker.
 * Cached in localStorage under 'od:volanalysis' for up to 4 hours.
 * Only fetched when user clicks ⟳ Sync Vol Analysis.
 */

import { useState, useCallback, useEffect } from 'react';
import { VolBar, SkewBadge, CacheBar, EmptyState, Spinner, ErrMsg, fmt, fmtPct,
         callAIEndpoint, loadLocalCache, saveLocalCache } from './EarningsShared';

/** Collapsible card for one volatility setup */
function VolCard({ item, t, expanded, onToggle }) {
  const structureColor = item.termStructure === 'backwardation' ? t.down : t.up;
  return (
    <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
      {/* Collapsed header */}
      <div
        onClick={onToggle}
        style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: '0 12px', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: expanded ? t.bgHover : 'transparent' }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: t.text }}>{item.ticker}</span>
            <span style={{ fontSize: 10, padding: '1px 6px', background: t.tagBg, color: t.tagText, borderRadius: 3 }}>{item.category}</span>
          </div>
          <div style={{ fontSize: 11, color: t.muted, marginTop: 1 }}>{item.name}</div>
        </div>
        <div style={{ textAlign: 'center', minWidth: 52 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: item.ivRank >= 70 ? t.down : item.ivRank >= 40 ? t.warn : t.up }}>{item.ivRank}</div>
          <div style={{ fontSize: 9, color: t.faint }}>IV Rank</div>
        </div>
        {/* IV-HV spread: positive = vol premium (selling premium is attractive) */}
        <div style={{ textAlign: 'center', minWidth: 52 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: (item.ivHvSpread ?? 0) > 3 ? t.down : t.muted }}>{fmt(item.ivHvSpread)}</div>
          <div style={{ fontSize: 9, color: t.faint }}>IV-HV sprd</div>
        </div>
        <div style={{ textAlign: 'center', minWidth: 76 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: structureColor }}>{item.termStructure || '—'}</div>
          <div style={{ fontSize: 9, color: t.faint }}>term str.</div>
        </div>
        <div style={{ color: t.faint, fontSize: 14, userSelect: 'none' }}>{expanded ? '▲' : '▼'}</div>
      </div>

      <div style={{ padding: '0 14px 8px' }}><VolBar score={item.volatilityScore} t={t} /></div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${t.border}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: `1px solid ${t.border}` }}>
            {[
              ['IV30',       `${fmt(item.iv30)}%`,        t.text],
              ['IV60',       `${fmt(item.iv60)}%`,        t.text],
              ['HV30',       `${fmt(item.hv30)}%`,        t.text],
              ['P/C Ratio',  fmt(item.putCallRatio),      (item.putCallRatio ?? 1) > 1.2 ? t.down : t.up],
              ['Key Level',  `$${fmt(item.keyLevel)}`,    t.accent],
              ['Level Type', item.keyLevelType || '—',    t.text],
              ['Skew',       <SkewBadge key="sk" skew={item.skew} t={t} />, null],
              ['Expiry',     item.nearestExpiry || '—',   t.muted],
            ].map(([lbl, val, color]) => (
              <div key={lbl} style={{ padding: '8px 12px', borderRight: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}` }}>
                <div style={{ fontSize: 9, color: t.muted, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: color || t.text }}>{val}</div>
              </div>
            ))}
          </div>
          {/* Trade idea */}
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 9, color: t.accent, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>💡 Trade Idea</div>
            <div style={{ fontSize: 13, color: t.text, fontFamily: 'monospace' }}>{item.tradeIdea}</div>
            {item.riskReward && <div style={{ fontSize: 11, color: t.muted, marginTop: 4 }}>Risk/Reward: {item.riskReward}</div>}
          </div>
          <div style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 9, color: t.muted, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Regime</div>
            <div style={{ fontSize: 12, color: t.muted }}>{item.regimeSummary}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VolAnalysisTab({ t }) {
  const [data,    setData]  = useState(null);
  const [cacheTs, setTs]    = useState(null);
  const [stale,   setStale] = useState(true);
  const [sharedCache, setSharedCache] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error,   setError]   = useState(null);
  const [expanded,setExpanded]= useState(0);

  const volMsg = () => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    return `Today is ${today}. Give me the top volatility trade setups for US equities and ETFs this week. Include the VIX term structure snapshot and your single best vol trade idea.`;
  };

  // Auto-load from KV cache on mount
  useEffect(() => {
    (async () => {
      setSyncing(true);
      try {
        const result = await callAIEndpoint('ai/volanalysis', volMsg(), false);
        setData(result);
        setTs(result.fetchedAt || Date.now());
        setStale(false);
        setSharedCache(result.sharedCache ?? false);
        saveLocalCache('od:volanalysis', result);
      } catch(e) {
        const local = loadLocalCache('od:volanalysis');
        if (local?.data) { setData(local.data); setTs(local.ts); setStale(true); }
        else setError(e.message);
      } finally { setSyncing(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = useCallback(async (force = true) => {
    setSyncing(true); setError(null);
    try {
      const result = await callAIEndpoint('ai/volanalysis', volMsg(), force);
      setData(result);
      setTs(result.fetchedAt || Date.now());
      setStale(false);
      setSharedCache(result.sharedCache ?? false);
      saveLocalCache('od:volanalysis', result);
      setExpanded(0);
    } catch(e) { setError(e.message); }
    finally { setSyncing(false); }
  }, []);

  const analyses = data?.analyses || [];

  return (
    <div>
      <CacheBar cacheTs={cacheTs} cacheStale={stale} weekRange={null} onSync={() => sync(true)} syncing={syncing} label="Vol Analysis" shared={sharedCache} />
      {error && <ErrMsg msg={error} onRetry={sync} />}

      {/* VIX term structure snapshot */}
      {data?.vixSnapshot && (() => {
        const vix = data.vixSnapshot;
        const structColor = vix.termStructure === 'backwardation' ? t.down : t.up;
        return (
          <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>VIX Snapshot</div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {[['Spot', vix.spot], ['1M Fut.', vix.futures1m], ['2M Fut.', vix.futures2m]].map(([l, v]) => (
                    <div key={l}>
                      <div style={{ fontSize: 9, color: 'var(--faint)', letterSpacing: '.08em' }}>{l}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{fmt(v, 1)}</div>
                    </div>
                  ))}
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--faint)', letterSpacing: '.08em' }}>Term structure</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: structColor, marginTop: 4 }}>{vix.termStructure}</div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>{vix.regimeLabel}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: vix.signal === 'bullish' ? t.up : vix.signal === 'bearish' ? t.down : t.muted }}>
                  {vix.signal?.toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Top idea banner */}
      {data?.topIdea && (
        <div style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
          <span style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>💡 Top idea this week · </span>
          <span style={{ fontSize: 13, color: 'var(--text)' }}>{data.topIdea}</span>
        </div>
      )}

      {data?.marketRegime && (
        <div style={{ padding: '8px 14px', marginBottom: 12, fontSize: 12, color: 'var(--muted)', borderLeft: '3px solid var(--border)' }}>
          {data.marketRegime}
        </div>
      )}

      {!data && !syncing && <EmptyState label="Vol Analysis" onSync={sync} syncing={syncing} />}
      {syncing && <Spinner />}
      {analyses.map((item, i) => (
        <VolCard key={item.ticker} item={item} t={t} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? -1 : i)} />
      ))}
    </div>
  );
}
